export type UploadedStudioMediaType = 'audio' | 'video';

export interface UploadedStudioLibraryItem {
  id: string;
  userId?: string;
  title: string;
  mediaType: UploadedStudioMediaType;
  mediaUrl: string;
  imageUrl?: string;
  uploadedAt: string;
  backendUrl?: string;
}

const STORAGE_KEY = 'haapa.uploadedStudioLibrary';
export const UPLOADED_STUDIO_LIBRARY_EVENT = 'haapa:uploaded-studio-library-updated';

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object';

const normalizeItem = (value: unknown): UploadedStudioLibraryItem | null => {
  if (!isRecord(value)) {
    return null;
  }

  const id = typeof value.id === 'string' ? value.id : '';
  const title = typeof value.title === 'string' ? value.title : '';
  const mediaType = value.mediaType === 'audio' || value.mediaType === 'video' ? value.mediaType : null;
  const mediaUrl = typeof value.mediaUrl === 'string' ? value.mediaUrl : '';
  const imageUrl = typeof value.imageUrl === 'string' ? value.imageUrl : undefined;
  const uploadedAt = typeof value.uploadedAt === 'string' ? value.uploadedAt : '';
  const backendUrl = typeof value.backendUrl === 'string' ? value.backendUrl : undefined;
  const userId = typeof value.userId === 'string' ? value.userId : undefined;

  if (!id || !title || !mediaType || !mediaUrl || !uploadedAt) {
    return null;
  }

  return {
    id,
    title,
    mediaType,
    mediaUrl,
    imageUrl,
    uploadedAt,
    backendUrl,
    userId,
  };
};

export const readUploadedStudioLibraryItems = (userId?: string): UploadedStudioLibraryItem[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return [];
    }

    const parsed = JSON.parse(rawValue) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((entry) => normalizeItem(entry))
      .filter((entry): entry is UploadedStudioLibraryItem => Boolean(entry))
      .filter((entry) => !userId || entry.userId === userId)
      .sort((left, right) => new Date(right.uploadedAt).getTime() - new Date(left.uploadedAt).getTime());
  } catch {
    return [];
  }
};

export const addUploadedStudioLibraryItem = (item: UploadedStudioLibraryItem) => {
  if (typeof window === 'undefined') {
    return;
  }

  const items = readUploadedStudioLibraryItems();
  const nextItems = [item, ...items.filter((entry) => entry.id !== item.id)].slice(0, 60);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextItems));
  window.dispatchEvent(new CustomEvent(UPLOADED_STUDIO_LIBRARY_EVENT));
};

export const deleteUploadedStudioLibraryItem = (itemId: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  const items = readUploadedStudioLibraryItems();
  const nextItems = items.filter((entry) => entry.id !== itemId);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextItems));
  window.dispatchEvent(new CustomEvent(UPLOADED_STUDIO_LIBRARY_EVENT));
};
